/**
 * LpMarquee
 * 横に流れ続ける帯（流れるテキスト・対応媒体ロゴ）の再生制御。
 *
 * 流す動き自体はCSSアニメーションが持ち、ここは**止める判断だけ**を担う。
 * - 画面外・タブ非表示のあいだは `is-paused` を付けて止める（見えていない間は動かさない）
 * - JSが動かなくても既定は「再生」なので、止まったまま固まることはない
 * - `prefers-reduced-motion` は最初から止める
 *
 * マークアップは、同じ並びを2つ入れて -50% ずらす前提（継ぎ目なくループさせるため）。
 *   <div class="p-lp-ticker js-lp-marquee">
 *     <div class="p-lp-ticker-track"><ul>…</ul><ul aria-hidden="true">…</ul></div>
 *   </div>
 *
 * **1セットが帯より狭いと、-50%へ近づくにつれ右端に空白が出る。**そのため原本を
 * 帯幅以上になるまで繰り返してから流す（LogoLoop と同じ手当て）。remがvw連動なので
 * 同じ帯域では比が変わらないが、SP/PCで比が変わるためリサイズで組み直す。
 */

export default class LpMarquee {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    if (!this.root) {
      return;
    }

    this.fill();

    let timer = null;
    this.onResize = () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => this.fill(), 200);
    };
    window.addEventListener('resize', this.onResize);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.root.classList.add('is-paused');
      return;
    }

    this.inView = false;
    this.pageVisible = !document.hidden;
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.observe();
  }

  /** 1セットが帯幅以上になるまで原本を繰り返す（2セットは常に同じ中身にする） */
  fill() {
    const track = this.root.firstElementChild;
    if (!track) {
      return;
    }
    const lists = Array.from(track.children);
    if (lists.length < 2) {
      return;
    }

    if (this.baseHTML == null) {
      this.baseHTML = lists[0].innerHTML;
    }
    lists.forEach((list) => {
      list.innerHTML = this.baseHTML;
    });

    const containerWidth = this.root.clientWidth;
    let guard = 0;
    while (lists[0].scrollWidth < containerWidth && guard < 50) {
      lists.forEach((list) => {
        list.insertAdjacentHTML('beforeend', this.baseHTML);
      });
      guard += 1;
    }
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      this.sync();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.inView = entry.isIntersecting;
        this.sync();
      });
    });
    this.observer.observe(this.root);

    // 監視の初回コールバックを待たず、今の位置で判定しておく
    const rect = this.root.getBoundingClientRect();
    this.inView = rect.bottom > 0 && rect.top < window.innerHeight;
    this.sync();
  }

  onVisibilityChange() {
    this.pageVisible = !document.hidden;
    this.sync();
  }

  /** 動かすのは「画面内 かつ タブ表示中」のときだけ */
  sync() {
    this.root.classList.toggle('is-paused', !(this.inView && this.pageVisible));
  }

  destroy() {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
